
import { useState, useEffect, useRef } from "react";

interface UMAPPoint {
  userId: string;
  x: number;
  y: number;
}

interface UMAPVisualizationProps {
  points: UMAPPoint[];
  currentUserId: string;
  loading?: boolean;
}

export default function UMAPVisualization({
  points,
  currentUserId,
  loading = false,
}: UMAPVisualizationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredPoint, setHoveredPoint] = useState<UMAPPoint | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  // Configurar tamaño del canvas
  useEffect(() => {
    const updateSize = () => {
      const container = canvasRef.current?.parentElement;
      if (container) {
        const width = container.clientWidth;
        const height = Math.min(400, width);
        setCanvasSize({ width, height });
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // Renderizar el canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || points.length === 0) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { width, height } = canvasSize;
    if (width === 0 || height === 0) return;

    const centerX = width / 2;
    const centerY = height / 2;
    const maxRadius = Math.min(width, height) / 2 - 20;

    // Limpiar canvas
    ctx.clearRect(0, 0, width, height);

    // Fondo
    ctx.fillStyle = "#f9fafb";
    ctx.fillRect(0, 0, width, height);

    // Dibujar anillos concéntricos desde el centro (usuario actual)
    const ringCount = 4;
    for (let i = ringCount; i >= 1; i--) {
      const radius = (maxRadius / ringCount) * i;
      
      // Relleno degradado sutil
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.strokeStyle = i === ringCount ? "#d1d5db" : "#e5e7eb";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Líneas cruzadas sutiles
    ctx.strokeStyle = "#e5e7eb";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    
    ctx.beginPath();
    ctx.moveTo(centerX - maxRadius, centerY);
    ctx.lineTo(centerX + maxRadius, centerY);
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(centerX, centerY - maxRadius);
    ctx.lineTo(centerX, centerY + maxRadius);
    ctx.stroke();
    
    ctx.setLineDash([]);

    // Etiquetas de distancia en los anillos
    ctx.fillStyle = "#9ca3af";
    ctx.font = "10px sans-serif";
    ctx.textAlign = "left";
    for (let i = 1; i <= ringCount; i++) {
      const radius = (maxRadius / ringCount) * i;
      const label = i === 1 ? "Muy similar" : i === 2 ? "Similar" : i === 3 ? "Diferente" : "Muy diferente";
      ctx.fillText(label, centerX + 4, centerY - radius + 12);
    }

    // Calcular la distancia de cada punto al centro para colorear por similitud
    const currentUserPoint = points.find(p => p.userId === currentUserId);
    
    // Dibujar líneas de conexión sutiles desde el centro a cada punto
    points.forEach((point) => {
      if (point.userId === currentUserId) return;
      
      const px = 20 + point.x * (width - 40);
      const py = 20 + (1 - point.y) * (height - 40);
      const dist = Math.sqrt(Math.pow(px - centerX, 2) + Math.pow(py - centerY, 2));
      const normalizedDist = Math.min(dist / maxRadius, 1);
      
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(px, py);
      ctx.strokeStyle = `rgba(168, 85, 247, ${0.08 + (1 - normalizedDist) * 0.12})`;
      ctx.lineWidth = 1;
      ctx.stroke();
    });

    // Dibujar puntos de otros usuarios (primero, para que el usuario actual quede encima)
    points.forEach((point) => {
      if (point.userId === currentUserId) return;

      const px = 20 + point.x * (width - 40);
      const py = 20 + (1 - point.y) * (height - 40);
      const isHovered = hoveredPoint?.userId === point.userId;

      // Calcular color basado en distancia al centro
      const dist = Math.sqrt(Math.pow(px - centerX, 2) + Math.pow(py - centerY, 2));
      const normalizedDist = Math.min(dist / maxRadius, 1);

      // Color: más cercano = más púrpura, más lejano = más gris
      const r = Math.round(168 + (156 - 168) * normalizedDist);
      const g = Math.round(85 + (163 - 85) * normalizedDist);
      const b = Math.round(247 + (175 - 247) * normalizedDist);
      const alpha = isHovered ? 1 : 0.7 + (1 - normalizedDist) * 0.3;

      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;

      ctx.beginPath();
      ctx.arc(px, py, isHovered ? 7 : 5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
      ctx.fill();

      if (isHovered) {
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    });

    // Dibujar punto del usuario actual (siempre en el centro)
    if (currentUserPoint) {
      const px = 20 + currentUserPoint.x * (width - 40);
      const py = 20 + (1 - currentUserPoint.y) * (height - 40);

      // Halo exterior
      ctx.beginPath();
      ctx.arc(px, py, 16, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(168, 85, 247, 0.15)";
      ctx.fill();

      // Halo medio
      ctx.beginPath();
      ctx.arc(px, py, 12, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(168, 85, 247, 0.25)";
      ctx.fill();

      // Punto principal con gradiente
      ctx.shadowColor = "rgba(168, 85, 247, 0.5)";
      ctx.shadowBlur = 12;
      
      ctx.beginPath();
      ctx.arc(px, py, 9, 0, Math.PI * 2);
      const gradient = ctx.createRadialGradient(px, py, 0, px, py, 9);
      gradient.addColorStop(0, "#c084fc");
      gradient.addColorStop(0.5, "#a855f7");
      gradient.addColorStop(1, "#ec4899");
      ctx.fillStyle = gradient;
      ctx.fill();

      // Borde blanco
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2.5;
      ctx.stroke();

      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;

      // Etiqueta "Tú"
      ctx.fillStyle = "#7c3aed";
      ctx.font = "bold 11px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Tú", px, py - 20);
    }

  }, [points, currentUserId, hoveredPoint, canvasSize]);

  // Manejar hover
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) * (canvasSize.width / rect.width);
    const mouseY = (e.clientY - rect.top) * (canvasSize.height / rect.height);

    const { width, height } = canvasSize;

    let closestPoint: UMAPPoint | null = null;
    let minDistance = Infinity;

    points.forEach((point) => {
      const px = 20 + point.x * (width - 40);
      const py = 20 + (1 - point.y) * (height - 40);
      const distance = Math.sqrt(
        Math.pow(mouseX - px, 2) + Math.pow(mouseY - py, 2)
      );

      if (distance < 15 && distance < minDistance) {
        closestPoint = point;
        minDistance = distance;
      }
    });

    setHoveredPoint(closestPoint);
    if (closestPoint) {
      setTooltipPosition({ 
        x: e.clientX - rect.left, 
        y: e.clientY - rect.top 
      });
    }
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };

  // Manejar touch
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || e.touches.length === 0) return;

    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const mouseX = (touch.clientX - rect.left) * (canvasSize.width / rect.width);
    const mouseY = (touch.clientY - rect.top) * (canvasSize.height / rect.height);

    const { width, height } = canvasSize;

    let closestPoint: UMAPPoint | null = null;
    let minDistance = Infinity;

    points.forEach((point) => {
      const px = 20 + point.x * (width - 40);
      const py = 20 + (1 - point.y) * (height - 40);
      const distance = Math.sqrt(
        Math.pow(mouseX - px, 2) + Math.pow(mouseY - py, 2)
      );

      if (distance < 20 && distance < minDistance) {
        closestPoint = point;
        minDistance = distance;
      }
    });

    setHoveredPoint(closestPoint);
    if (closestPoint) {
      setTooltipPosition({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      });
    }
  };

  const handleTouchEnd = () => {
    setTimeout(() => setHoveredPoint(null), 2000);
  };

  if (loading) {
    return (
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">
          Mapa de Similitud (UMAP)
        </h2>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-500 text-sm">Generando visualización...</p>
          </div>
        </div>
      </div>
    );
  }

  if (points.length === 0) {
    return (
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">
          Mapa de Similitud (UMAP)
        </h2>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-3">
              <i className="ri-map-pin-line text-gray-400 text-3xl"></i>
            </div>
            <p className="text-gray-500 text-sm">
              No hay suficientes datos para generar el mapa
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-6">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800 mb-2">
          Mapa de Similitud (UMAP)
        </h2>
        <p className="text-gray-500 text-xs">
          Tú estás en el centro. Los más cercanos son los más parecidos a ti.
        </p>
      </div>

      {/* Canvas */}
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          className="w-full rounded-xl cursor-crosshair"
          style={{ touchAction: "none" }}
        />

        {/* Tooltip */}
        {hoveredPoint && (
          <div
            className="absolute bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg pointer-events-none z-10"
            style={{
              left: `${tooltipPosition.x + 10}px`,
              top: `${tooltipPosition.y - 40}px`,
              transform: "translateX(-50%)",
            }}
          >
            <div className="font-medium">
              {hoveredPoint.userId === currentUserId
                ? "Tú"
                : `Usuario ${hoveredPoint.userId.slice(0, 8)}`}
            </div>
          </div>
        )}
      </div>

      {/* Leyenda */}
      <div className="mt-4 flex items-center justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 shadow-lg"></div>
          <span className="text-gray-600">Tu perfil (centro)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-purple-300"></div>
          <span className="text-gray-600">Otros usuarios</span>
        </div>
      </div>

      {/* Descripción */}
      <div className="mt-4 p-3 bg-purple-50 rounded-xl">
        <p className="text-xs text-gray-600 leading-relaxed">
          <span className="font-semibold text-purple-600">¿Cómo leer este mapa?</span>
          <br />
          Tú siempre estás en el centro. Los anillos indican niveles de similitud: 
          los usuarios más cercanos al centro comparten un perfil más parecido al tuyo. 
          Los colores más intensos también indican mayor similitud.
        </p>
      </div>
    </div>
  );
}
